import { useState, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import Button from 'react-md/lib/Buttons'
import FontIcon from 'react-md/lib/FontIcons'
import styled from 'styled-components'
import PaymentProductCreateDialog from 'ui/ManagerFinance/PaymentProducts/PaymentProductCreateDialog'
import PaymentProductsTable from 'ui/ManagerFinance/PaymentProducts/PaymentProductsTable'
import useValueAddedTaxes from 'ui/ManagerFinance/ValueAddedTax/useValueAddedTaxes'
import { createVatOptions } from 'ui/ManagerFinance/utils/vatOptions'
import usePaymentProducts from 'ui/ManagerFinance/PaymentProducts/usePaymentProducts'
import PaymentProductEditDialog from 'ui/ManagerFinance/PaymentProducts/PaymentProductEditDialog'
import LoanCreateContract from './LoanCreateContract'
import type { SelectOptions } from 'ui/ManagerFinance/types'
import useChartOfAccounts from 'ui/ManagerFinance/Settings/ChartOfAccountSettings/useChartOfAccounts'
import { createChartOfAccountsOptions } from 'ui/ManagerFinance/utils/createChartOfAccountsOptions'
import type { LoanCreateFormInputs } from './validation'
import type { ControlledSelectableSharegroup } from '../types'

const AddProductPaymentButton = styled(Button)`
	display: flex;
	flex-flow: row nowrap;
	align-items: center;
	justify-content: center;
	margin-top: 0.5rem;
	gap: 0.5rem;
`

const CreatePaymentProductContainer = styled.div`
	padding: 0 1.5rem;
	padding-bottom: 7.5rem;
`
type Props = {
	onPaymentProductDefined: (paymentProductUUID?: string) => void
	sharegroups: ControlledSelectableSharegroup[]
	companyOptions: SelectOptions[]
	selectedFinanceCompanyUUID: string
}

function LoanCreatePaymentProduct({
	onPaymentProductDefined,
	sharegroups,
	companyOptions,
	selectedFinanceCompanyUUID,
}: Props) {
	const [createPaymentProductDialogOpen, setCreatePaymentProductDialogOpen] =
		useState(false)

	const [editingPaymentProduct, setEditingPaymentProduct] = useState<
		string | null
	>(null)

	const { watch } = useFormContext<LoanCreateFormInputs>()

	const { valueAddedTaxes } = useValueAddedTaxes()

	const { chartOfAccounts } = useChartOfAccounts(selectedFinanceCompanyUUID)

	const [companyUUID, loanUUID] = watch(['companyUUID', 'uuid'])

	const chosenSharegroups = useMemo(() => {
		return sharegroups.filter((sg) => sg.selected)
	}, [sharegroups])

	const vatOptions = createVatOptions(valueAddedTaxes || [])

	const chartOfAccountsOptions = createChartOfAccountsOptions(
		chartOfAccounts || [],
	)

	const paymentProductFilters = useMemo(
		() => ({
			loanUUID,
			separateBilling: null,
			vatUUID: null,
			date: null,
			searchText: null,
		}),
		[loanUUID],
	)

	const paymentProducts = usePaymentProducts(companyUUID, paymentProductFilters)
	const onEditPaymentProduct = (uuid: string) => {
		setEditingPaymentProduct(uuid)
	}

	const contractsDefined =
		chosenSharegroups.filter(
			(apt) => apt.contractUUID !== null && apt.contractUUID !== undefined,
		).length === chosenSharegroups.length

	useEffect(() => {
		if (onPaymentProductDefined) {
			if (paymentProducts.data.length > 0) {
				onPaymentProductDefined(paymentProducts.data[0].uuid)
			} else {
				onPaymentProductDefined(undefined)
			}
		}
	}, [paymentProducts.data, onPaymentProductDefined])

	return (
		<CreatePaymentProductContainer>
			<p style={{ marginBottom: '2rem' }}>
				<FormattedMessage
					defaultMessage="Jotta voimme yhdistää vastikelaskuja lainalle, on ensin määriteltävä sopimukset jokaiselle valitulle osakeryhmälle/huoneistolle. Sen jälkeen luodaan lainan vastiketta varten maksulaji. Lopuksi lainaa luodessa liitämme maksulajin sopimuksille."
					description="In order to link service charge invoices to a loan, you must first create a payment type for the loan's service charge. After that, you can add the payment type you created to the shareholders' agreements."
				/>
			</p>
			<LoanCreateContract
				sharegroups={sharegroups}
				companyOptions={companyOptions}
			/>
			<strong>
				<FormattedMessage
					defaultMessage="Vastikkeen maksulaji"
					description="Loan payment product"
				/>
			</strong>
			{contractsDefined ? (
				<>
					{!loanUUID || paymentProducts.data.length === 0 ? (
						<div>
							<AddProductPaymentButton
								secondary
								flat
								onClick={() => setCreatePaymentProductDialogOpen(true)}
							>
								<FontIcon
									style={{
										color: 'var(--color-secondary-dark)',
									}}
								>
									add
								</FontIcon>
								<FormattedMessage
									defaultMessage="Lisää uusi maksulaji"
									description="Button label for adding new date"
								/>
							</AddProductPaymentButton>
						</div>
					) : (
						<>
							<PaymentProductsTable
								paymentProducts={paymentProducts.data}
								vatOptions={vatOptions}
								refresh={paymentProducts.refresh}
								onEditPaymentProduct={onEditPaymentProduct}
							/>
							<div style={{ marginTop: '1rem' }}>
								<FormattedMessage
									defaultMessage="Kun luot lainan, maksulaji lisätään automaattisesti valitsemillesi sopimuksille."
									description="When you create a loan, the payment product is automatically added to the selected contracts."
								/>
							</div>
						</>
					)}
				</>
			) : (
				<p>
					<FormattedMessage
						defaultMessage="Ennen kuin voit määritellä vastikkeille maksunlajin, sinun on määriteltävä jokaiselle huoneistolle sopimus."
						description="Before you can assign payment products for the loan, you must assign contracts to each apartment"
					/>
				</p>
			)}

			{createPaymentProductDialogOpen && (
				<PaymentProductCreateDialog
					companyUUID={companyUUID}
					loanUUID={loanUUID}
					onHide={() => setCreatePaymentProductDialogOpen(false)}
					refreshPaymentProducts={paymentProducts.refresh}
					paymentProducts={paymentProducts.data}
					vatOptions={vatOptions}
					chartOfAccountsOptions={chartOfAccountsOptions}
				/>
			)}
			{editingPaymentProduct && (
				<PaymentProductEditDialog
					paymentProductUUID={editingPaymentProduct}
					onHide={() => setEditingPaymentProduct(null)}
					refreshPaymentProducts={paymentProducts.refresh}
					paymentProducts={paymentProducts.data}
					vatOptions={vatOptions}
					chartOfAccountsOptions={chartOfAccountsOptions}
				/>
			)}
		</CreatePaymentProductContainer>
	)
}

export default LoanCreatePaymentProduct
